
import { MultiFileService } from "@/services/MultiFileService.js";
import {required, maxLength, requiredIf} from "vuelidate/lib/validators";
import Select2Multiple from "v-select2-multiple-component";
import flatPickr from 'vue-flatpickr-component';
import upLoad from '@/assets/img/upload_photo3.jpg';
import axios from "axios";
import CONFIG from "@/assets/js/config.js";

export default {
  name: 'TeacherLeaveForm',
  components: {
    flatPickr,
    Select2Multiple,
  },

  props: {
    leaveData: { type: Object, required: true },
    submitted: { type: Boolean, default: false },
    images: { type: Array, default: () => [] },
    existingImages: { type: Array, default: () => [] },
    leaveTypeOptions: { type: Array, default: () => [] },
    tbdId: { type: Number, default: null },
  },

  emits: ["update:leaveData", "update:images", "update:existingImages", 'validations'],

  data() {
    return {
      localLeaveData: this.initializeData(this.leaveData),
      localImages: [...this.images],
      localExistingImages: [...this.existingImages],
      upLoad,
      periodOptions: {},
      CONFIG,
      dateRange: [],
      duplicateError: false,
      dateError: '',
      leaveBalanceList: []
    };
  },

  validations: {
    localLeaveData: {
      period: {
        required,
      },
      from_date: {
        required,
      },
      selected_leave_type: {
        required: requiredIf(function() {
          return this.localLeaveData.status === this.CONFIG.SHIFT_LEAVE_STATUS.APPROVE
                  && this.localLeaveData.selected_leave_type !== this.tbdId;
        })
      },
      to_date: {
        required,
        afterOrEqualFromDate(value) {
          if (!value || !this.localLeaveData.from_date) return true;
          return new Date(value) >= new Date(this.localLeaveData.from_date);
        },
      },
      status: {
        required,
      },
      leave_date: {
        required: function (value) {
          if (!this.localLeaveData.id) {
            return true;
          }
          return !!value;
        }
      },
      remark: {
        required,
        maxLength: maxLength(1000)
      }
    },
  },

  methods: {
    initializeData(data) {
      let init = { ...data };
      const tbdId = this.tbdId;
      if (init.leave_type_id && init.leave_type_id !== tbdId) {
        init.selected_leave_type = init.leave_type_id;
      } else {
        init.selected_leave_type = null;
      }
      if (init.leave_date) {
        init.from_date = init.from_date || init.leave_date;
        init.to_date = init.to_date || init.leave_date;
      }
      return init;
    },

    chooseFiles(id) {
      MultiFileService.chooseFiles(id);
    },

    onFileChange(e) {
      MultiFileService.onFileChange({
        event: e,
        newFiles: this.localImages,
        existingFiles: this.localExistingImages,
        allowedFileTypes: ["image/*"],
        maxFileSize: 4, // 4 MB
        maxFileCount: 5,
        refs: this.$refs
      });
    },

    formatTime(time) {
      if (!time) return;
      return time.slice(0, 5);
    },

    handleSingleDateChange(newDate) {
      this.localLeaveData.leave_date = newDate;
      this.localLeaveData.from_date = newDate;
      this.localLeaveData.to_date = newDate;

      if (this.localLeaveData.period && Object.keys(this.periodOptions).length > 0) {
        const formatted = {};
        formatted[newDate] = this.periodOptions[newDate] || {};
        this.localLeaveData.period = [];
        Object.entries(formatted[newDate]).forEach(([shiftKey, periods]) => {
          periods.forEach(p => {
            this.localLeaveData.period.push({
              date: newDate,
              shift: shiftKey.replace('shift', ''),
              from: p.from,
              to: p.to
            });
          });
        });
      }
    },

    fetchShiftByDate() {
      const params = {
        teacher_id: this.localLeaveData.teacher_id,
        from_date: this.localLeaveData.leave_date || this.localLeaveData.from_date,
        to_date: this.localLeaveData.leave_date || this.localLeaveData.to_date
      }
      if (!params.from_date || !params.to_date) return;
      axios.get('teacher/leave/shifts-by-day', { params })
        .then((response) => {
          if (!this.localLeaveData.id) {
            this.localLeaveData.period = []
          }
          if (response.data?.request_date) {
            const formatted = {}
            Object.entries(response.data.request_date).forEach(([date, shifts]) => {
              formatted[date] = {}

              Object.entries(shifts).forEach(([shiftKey, periods]) => {
                const shiftNo = shiftKey.replace('Shift', '')

                formatted[date][shiftKey] = periods.map(p => ({
                  shift: shiftNo,
                  from: p.from_time,
                  to: p.to_time
                }))
              })
            })
            this.periodOptions = formatted;
            if (this.localLeaveData.id && this.localLeaveData.period) {
              const newPeriods = [];

              this.localLeaveData.period.forEach(saved => {
                const dailyOptions = this.periodOptions[saved.date];
                if (!dailyOptions) return;

                Object.entries(dailyOptions).forEach(([shiftKey, options]) => {
                  options.forEach(opt => {
                    const savedFrom = saved.from.slice(0,5);
                    const savedTo = saved.to.slice(0,5);

                    const optFrom = opt.from.slice(0,5);
                    const optTo = opt.to.slice(0,5);

                    const overlap = !(savedTo <= optFrom || savedFrom >= optTo);

                    if (overlap) {
                      newPeriods.push({
                        date: saved.date,
                        shift: shiftKey.replace('Shift',''),
                        from: opt.from,
                        to: opt.to
                      });
                    }
                  });
                });
              });

              this.localLeaveData.period = newPeriods;
            }
            if (!this.localLeaveData.id) {
              this.handleDateRangeAutoSelect()
            }
            this.$emit('update:periodOptions', this.periodOptions);
          }
        })
        .catch((err) => {
          if (err.response && err.response.status == 401) {
            this.$store.commit('logout');
            window.location.replace('login');
          }
          this.isLoading = false;
          this.periodOptions = {}
        })
    },

    toggleFullDay(date, shifts, checked) {
      if (!Array.isArray(this.localLeaveData.period)) {
        this.$set(this.localLeaveData, 'period', []);
      }

      if (checked) {
        Object.entries(shifts).forEach(([shiftKey, periods]) => {
          periods.forEach(p => {
            if (!this.isPeriodSelected(date, shiftKey, p)) {
              this.togglePeriod(date, shiftKey, p, true)
            }
          })
        })
      } else {
        this.localLeaveData.period =
          this.localLeaveData.period.filter(el => el.date !== date)
      }
    },

    handleDateRangeAutoSelect() {
      const from = this.localLeaveData.from_date;
      const to = this.localLeaveData.to_date;
      if (from && to && from !== to) {

        if (Object.keys(this.periodOptions).length > 0) {
          Object.entries(this.periodOptions).forEach(([date, shifts]) => {
            this.toggleFullDay(date, shifts, true);
          });
        }
      }
    },

    togglePeriod(date, shiftKey, p, checked) {
      if (!Array.isArray(this.localLeaveData.period)) {
        this.$set(this.localLeaveData, 'period', []);
      }
      const shiftNo = shiftKey.replace('Shift', '');

      if (checked) {
        const exists = this.localLeaveData.period.some(el => 
          el.date === date && el.shift === shiftNo && el.from === p.from && el.to === p.to
        );
        if (!exists) {
          this.localLeaveData.period.push({
            date: date,
            shift: shiftNo,
            from: p.from,
            to: p.to
          });
        }
      } else {
        this.localLeaveData.period = this.localLeaveData.period.filter(el => {
          const isMatch = (el.date === date && String(el.shift) === String(shiftNo));

          if (isMatch) {
            if (el.from === p.from && el.to === p.to) return false;
            this.localLeaveData.period = this.localLeaveData.period.filter(el => {
              return !(
                el.date === date &&
                String(el.shift) === String(shiftNo) &&
                el.from === p.from &&
                el.to === p.to
              );
            });
          }
          return true;
        });
      }
    },

    toggleShift(date, shiftKey, periods, checked) {
      periods.forEach(p =>
        this.togglePeriod(date, shiftKey, p, checked)
      )
    },

    isPeriodSelected(date, shiftKey, p) {
      if (!this.localLeaveData.period || this.localLeaveData.period.length === 0) return false;

      return this.localLeaveData.period.some(el => {
        const dateMatch = el.date === date;
        if (!dateMatch) return false;

        const dbFrom = el.from ? el.from.slice(0, 5) : '';
        const dbTo = el.to ? el.to.slice(0, 5) : '';
        const optFrom = p.from ? p.from.slice(0, 5) : '';
        const optTo = p.to ? p.to.slice(0, 5) : '';

        return dbFrom === optFrom && dbTo === optTo;
      });
    },

    isDateFullySelected(date, shifts) {
      const allShifts = Object.entries(shifts);
      if (allShifts.length === 0) return false;

      return allShifts.every(([shiftKey, periods]) => {
        return periods.every(p => this.isPeriodSelected(date, shiftKey, p));
      });
    },

    isShiftFullySelected(date, shiftKey, periods) {
      if (!periods || periods.length === 0) return false;
      return periods.every(p => this.isPeriodSelected(date, shiftKey, p));
    },

    removeImage(i) {
      MultiFileService.removeFile(i, this.localImages);
    },

    removeExistingImage(id) {
      const indexInExistingImages = this.localExistingImages.findIndex(img => img.id === id);
      if (indexInExistingImages !== -1) {
        MultiFileService.removeFile(indexInExistingImages, this.localExistingImages);
      }
    },

    checkAndFetchShifts() {
      const { teacher_id, from_date, to_date } = this.localLeaveData;
      if (teacher_id && from_date && to_date) {
        this.fetchShiftByDate();
        this.getLeaveBalances(teacher_id)
      }
    },

    autoSelectLeaveType() {
      if (!this.localLeaveData.selected_leave_type && this.leaveTypeOptions && this.leaveTypeOptions.length > 0) {
        this.localLeaveData.selected_leave_type = this.leaveTypeOptions[0].id;

        if (this.$v.localLeaveData.selected_leave_type) {
          this.$v.localLeaveData.selected_leave_type.$touch();
        }
      }
    },

    async getLeaveBalances(teacherId) {
      if (!teacherId) return;
      const params = {
        'teacher_id' : this.leaveData.teacher_id
      }
      try {
        const res = await axios.get('/leaveType/type-byOwner', { params });
        this.leaveBalanceList = res.data;
      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$store.commit('logout');
          window.location.replace('login');
        }
      }
    }
  },

  watch: {
    tbdId: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.leaveData) {
          this.localLeaveData = this.initializeData(this.leaveData);
        }
      }
    },

    leaveData: {
      deep: true,
      handler(newVal) {
        if (newVal && newVal.id !== this.localLeaveData.id) {
          this.localLeaveData = this.initializeData(newVal);
          if (this.existingImages.length) this.localExistingImages = [...this.existingImages];
        }
      }
    },

    // Watch localLeaveData → emit to parent when changed
    localLeaveData: {
      deep: true,
      handler(newVal) {
        this.$emit('update:leaveData', newVal);
      },
    },

    localImages: {
      deep: true,
      handler(newVal) {
        this.$emit('update:images', newVal);
      },
    },

    localExistingImages: {
      deep: true,
      handler(newVal) {
        this.$emit('update:existingImages', newVal);
      },
    },

    // If parent componement updates teacher_id, sync back to locals
    'leaveData.teacher_id': {
      deep: false,
      handler(newVal) {
        this.localLeaveData.teacher_id = newVal;
        this.checkAndFetchShifts()
        this.getLeaveBalances(newVal)
      },
    },

    'localLeaveData.from_date': {
      handler() {
        this.checkAndFetchShifts()
        this.handleDateRangeAutoSelect()
      },
    },

    'localLeaveData.to_date': {
      handler() {
        this.checkAndFetchShifts()
        this.handleDateRangeAutoSelect()
      },
    },

    'localLeaveData.leave_date': {
      handler(newVal) {
        if (!newVal) return;
        this.localLeaveData.from_date = newVal;
        this.localLeaveData.to_date = newVal;
        if (this.localLeaveData.id) return
        this.checkAndFetchShifts();
      }
    },

    'selectedPeriods.fullDay'(val) {
      if (val) {
        const newShifts = {};
        for (const shift in this.groupedShifts) {
          newShifts[shift] = this.groupedShifts[shift].map(p => `${p.from}-${p.to}`);
        }
        this.selectedPeriods.shifts = newShifts;
      } else {
        const totalBlocks = this.periodOptions.length;
        let selectedBlocks = 0;
        for (const s in this.selectedPeriods.shifts) {
          selectedBlocks += this.selectedPeriods.shifts[s].length;
        }
        if (selectedBlocks === totalBlocks) {
          this.selectedPeriods.shifts = {};
        }
      }
    },

    leaveTypeOptions: {
    deep: true,
    immediate: true,
    handler(newOptions) {
      if (!newOptions || newOptions.length === 0) return;
      if (this.localLeaveData.id && this.localLeaveData.leave_type_id) {
        this.localLeaveData.selected_leave_type = this.localLeaveData.leave_type_id;
      }
    }
  },

    'localLeaveData.selected_leave_type'(newVal) {
      if (!newVal) return;
      if (this.localLeaveData.id) {
        this.localLeaveData.leave_type_id = newVal;
      }
      if (this.$v && this.$v.localLeaveData.selected_leave_type) {
        this.$v.localLeaveData.selected_leave_type.$reset();
      }
    },

    'localLeaveData.status': {
      handler(newVal) {
        if (newVal !== this.CONFIG.SHIFT_LEAVE_STATUS.APPROVE) {
          this.localLeaveData.selected_leave_type = null;
        } else {
          if (this.localLeaveData.selected_leave_type === this.tbdId) {
            this.localLeaveData.selected_leave_type = null
          }
        }
      }
    },

    selectedPeriods: {
      deep:true,
      handler() {
        const formattedPeriods = this.prepareToPeriodRequest();
        this.localLeaveData.period = formattedPeriods;
      } 
    },
  },

  computed: {
    validDates() {
      const startDate = this.localLeaveData.from_date;
      const endDate = this.localLeaveData.to_date;

      if (!startDate || !endDate) {
        return false;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (end < start) {
        this.dateError = 'To date must be equal or after from date';
        return false;
      }
      return true;
    }
  },

  mounted() {
    this.checkAndFetchShifts();
    //then emit validations to parent
    this.$nextTick(() => {
      if (this.$v) {
        this.$emit("validations", this.$v);
      }
    });
  },
}
